/**
 * The one-line notice channel: a message from code that is not rendering
 * anything of its own.
 *
 * Built for #3072. Preference writes fire from module scope (the sidebar's
 * debounced sync, DataTable's) and every caller swallowed the failure, so a
 * save that never landed looked saved until the next page load quietly showed
 * the old value. The helper needed somewhere to say so, and the repo has no
 * toast library: UpdateToast (#769) is a single-purpose component, not a path
 * other code can post to.
 *
 * Deliberately small: one current notice, replaced rather than queued, cleared
 * by the reader. A second failure while one is showing is the same news.
 * NoticeToast renders it once, in the app shell.
 */
export interface AppNotice {
  /** New on every publish, so a repeat of the same text still restarts the timer. */
  id: number;
  text: string;
  /** #3597: "success" confirms a write that took (a retired family leaves
   *  the list, so the row itself cannot say so). Default: a warning. */
  tone: "warning" | "success";
}

let current: AppNotice | null = null;
let nextId = 1;
const listeners = new Set<(notice: AppNotice | null) => void>();

function emit() {
  for (const fn of listeners) fn(current);
}

/** Show one short line. Replaces anything already showing. */
export function showNotice(text: string, tone: AppNotice["tone"] = "warning"): void {
  current = { id: nextId++, text, tone };
  emit();
}

export function dismissNotice(): void {
  if (!current) return;
  current = null;
  emit();
}

export function currentNotice(): AppNotice | null {
  return current;
}

export function subscribeNotice(fn: (notice: AppNotice | null) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
